import { useMemo, useState } from 'react';
import {
  Flex,
  Select,
  Text,
  ToggleButton,
  ToggleButtonGroup,
} from '@backstage/ui';
import type { CheckFilter } from '@internal/backstage-plugin-pulse-common';
import { useCheckEntityPicker } from '../../../../hooks';
import { selectedKeys } from '../../../select-helpers';
import {
  buildFilterRules,
  parseRuleScope,
  pruneTypes,
  typeOptionsFor,
  type AnnotationFilter,
  type ScopeFacet,
  type ScopeFacetValues,
} from '../../../scope-filters/scope-filter-model';
import { ScopeFacetFields } from '../../../scope-filters/scope-facet-fields';
import { FieldError } from './field-error';

export type TrackFiltersSectionProps = {
  includeConditions: CheckFilter[];
  onIncludeConditionsChange: (conditions: CheckFilter[]) => void;
  excludeConditions: CheckFilter[];
  onExcludeConditionsChange: (conditions: CheckFilter[]) => void;
  excludeWithoutIncludeError?: string;
  isReadOnly?: boolean;
};

export const TrackFiltersSection = ({
  includeConditions,
  onIncludeConditionsChange,
  excludeConditions,
  onExcludeConditionsChange,
  excludeWithoutIncludeError,
  isReadOnly = false,
}: TrackFiltersSectionProps) => {
  const {
    kindOptions,
    typesByKind,
    lifecycleOptions,
    ownerOptions,
    tagOptions,
    annotationOptions,
  } = useCheckEntityPicker();
  const [visibleModes, setVisibleModes] = useState<Set<string>>(() => {
    const modes = new Set<string>(['include']);
    if (excludeConditions.length) {
      modes.add('exclude');
    }
    return modes;
  });

  const [initialIncludeScope] = useState(() =>
    parseRuleScope(includeConditions),
  );
  const [initialExcludeScope] = useState(() =>
    parseRuleScope(excludeConditions),
  );
  const [includeKinds, setIncludeKinds] = useState(initialIncludeScope.kinds);
  const [includeTypeKeys, setIncludeTypeKeys] = useState(
    initialIncludeScope.typeKeys,
  );
  const [excludeKinds, setExcludeKinds] = useState(initialExcludeScope.kinds);
  const [excludeTypeKeys, setExcludeTypeKeys] = useState(
    initialExcludeScope.typeKeys,
  );
  const [includeFacets, setIncludeFacets] = useState<ScopeFacetValues>(
    initialIncludeScope.facets,
  );
  const [excludeFacets, setExcludeFacets] = useState<ScopeFacetValues>(
    initialExcludeScope.facets,
  );
  const [includeAnnotations, setIncludeAnnotations] = useState<
    AnnotationFilter[]
  >(initialIncludeScope.annotations);
  const [excludeAnnotations, setExcludeAnnotations] = useState<
    AnnotationFilter[]
  >(initialExcludeScope.annotations);
  const facetOptions: Record<ScopeFacet, string[]> = {
    'spec.lifecycle': lifecycleOptions,
    'spec.owner': ownerOptions,
    'metadata.tags': tagOptions,
  };
  const kindSelectOptions = kindOptions.map((kind: string) => ({
    value: kind.toLowerCase(),
    label: kind,
  }));
  const includeTypeOptions = useMemo(
    () => typeOptionsFor(includeKinds, typesByKind),
    [includeKinds, typesByKind],
  );
  const excludeTypeOptions = useMemo(
    () => typeOptionsFor(excludeKinds, typesByKind),
    [excludeKinds, typesByKind],
  );

  const updateScope = (
    mode: 'include' | 'exclude',
    kinds: string[],
    typeKeys: string[],
    facets: ScopeFacetValues,
    annotations: AnnotationFilter[],
  ) => {
    const conditions = buildFilterRules(kinds, typeKeys, facets, annotations);

    if (mode === 'include') {
      onIncludeConditionsChange(conditions);
    } else {
      onExcludeConditionsChange(conditions);
    }
  };

  const includeActive = visibleModes.has('include');
  const excludeActive = visibleModes.has('exclude');

  return (
    <Flex direction="column" gap="3">
      <Text variant="title-x-small">Scope Filter</Text>
      <Text variant="body-small" color="secondary">
        Choose which entities this track applies to. Use Including to target
        kinds and types, and Excluding to leave some out.
      </Text>

      <ToggleButtonGroup
        selectionMode="multiple"
        selectedKeys={visibleModes}
        isDisabled={isReadOnly}
        onSelectionChange={selected => {
          const modes = new Set(Array.from(selected).map(String));
          setVisibleModes(modes);
          if (!modes.has('include')) {
            onIncludeConditionsChange([]);
            setIncludeKinds([]);
            setIncludeTypeKeys([]);
            setIncludeFacets({});
            setIncludeAnnotations([]);
          }
          if (!modes.has('exclude')) {
            onExcludeConditionsChange([]);
            setExcludeKinds([]);
            setExcludeTypeKeys([]);
            setExcludeFacets({});
            setExcludeAnnotations([]);
          }
        }}
        aria-label="Filter mode"
      >
        <ToggleButton key="include" id="include" size="small">
          Including
        </ToggleButton>
        <ToggleButton key="exclude" id="exclude" size="small">
          Excluding
        </ToggleButton>
      </ToggleButtonGroup>

      {includeActive ? (
        <>
          <Select
            label="Include kinds"
            description="Select at least one kind this track applies to"
            selectionMode="multiple"
            options={kindSelectOptions}
            value={includeKinds}
            isDisabled={isReadOnly}
            onChange={keys => {
              const kinds = selectedKeys(keys);
              const typeKeys = pruneTypes(kinds, includeTypeKeys);
              setIncludeKinds(kinds);
              setIncludeTypeKeys(typeKeys);
              updateScope(
                'include',
                kinds,
                typeKeys,
                includeFacets,
                includeAnnotations,
              );
            }}
          />
          <Flex
            direction="column"
            pl="4"
            style={{ borderLeft: '2px solid var(--bui-border-1, #ccc)' }}
          >
            <Select
              label="Include types"
              description="Optionally narrow to specific entity types (spec.type)."
              selectionMode="multiple"
              options={includeTypeOptions}
              value={includeTypeKeys}
              isDisabled={isReadOnly}
              onChange={keys => {
                const typeKeys = selectedKeys(keys);
                setIncludeTypeKeys(typeKeys);
                updateScope(
                  'include',
                  includeKinds,
                  typeKeys,
                  includeFacets,
                  includeAnnotations,
                );
              }}
            />
          </Flex>
          <ScopeFacetFields
            mode="include"
            options={facetOptions}
            values={includeFacets}
            onChange={facets => {
              setIncludeFacets(facets);
              updateScope(
                'include',
                includeKinds,
                includeTypeKeys,
                facets,
                includeAnnotations,
              );
            }}
            annotationOptions={annotationOptions}
            annotations={includeAnnotations}
            onAnnotationsChange={annotations => {
              setIncludeAnnotations(annotations);
              updateScope(
                'include',
                includeKinds,
                includeTypeKeys,
                includeFacets,
                annotations,
              );
            }}
            isDisabled={isReadOnly}
          />
        </>
      ) : null}

      {excludeActive ? (
        <>
          <Select
            label="Exclude kinds"
            description="Entities of these kinds are excluded."
            selectionMode="multiple"
            options={kindSelectOptions}
            value={excludeKinds}
            isDisabled={isReadOnly}
            onChange={keys => {
              const kinds = selectedKeys(keys);
              const typeKeys = pruneTypes(kinds, excludeTypeKeys);
              setExcludeKinds(kinds);
              setExcludeTypeKeys(typeKeys);
              updateScope(
                'exclude',
                kinds,
                typeKeys,
                excludeFacets,
                excludeAnnotations,
              );
            }}
          />
          <Flex
            direction="column"
            pl="4"
            style={{ borderLeft: '2px solid var(--bui-border-1, #ccc)' }}
          >
            <Select
              label="Exclude types"
              description="Entities of these types (spec.type) are excluded."
              selectionMode="multiple"
              options={excludeTypeOptions}
              value={excludeTypeKeys}
              isDisabled={isReadOnly}
              onChange={keys => {
                const typeKeys = selectedKeys(keys);
                setExcludeTypeKeys(typeKeys);
                updateScope(
                  'exclude',
                  excludeKinds,
                  typeKeys,
                  excludeFacets,
                  excludeAnnotations,
                );
              }}
            />
          </Flex>
          <ScopeFacetFields
            mode="exclude"
            options={facetOptions}
            values={excludeFacets}
            onChange={facets => {
              setExcludeFacets(facets);
              updateScope(
                'exclude',
                excludeKinds,
                excludeTypeKeys,
                facets,
                excludeAnnotations,
              );
            }}
            annotationOptions={annotationOptions}
            annotations={excludeAnnotations}
            onAnnotationsChange={annotations => {
              setExcludeAnnotations(annotations);
              updateScope(
                'exclude',
                excludeKinds,
                excludeTypeKeys,
                excludeFacets,
                annotations,
              );
            }}
            isDisabled={isReadOnly}
          />
          <FieldError message={excludeWithoutIncludeError} />
        </>
      ) : null}
    </Flex>
  );
};
